import { useState } from "react";
import { useUsers, UserWithRoles } from "@/hooks/useUsers";
import { useAuth } from "@/hooks/useAuth";
import { AppRole } from "@/lib/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function UserManagement() {
  const { users, isLoading, createUser, updateUser, updateUserStatus } = useUsers();
  const { user: currentUser } = useAuth();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [position, setPosition] = useState("");
  const [selectedRole, setSelectedRole] = useState<AppRole | "">("");
  const [editingUser, setEditingUser] = useState<UserWithRoles | null>(null);
  const [pendingStatusChange, setPendingStatusChange] = useState<{ user: UserWithRoles; newActive: boolean } | null>(null);

  const availableRoles = ["admin", "agent", "team_leader"];

  const resetForm = () => {
    setEmail("");
    setName("");
    setPosition("");
    setSelectedRole("");
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !name) {
      toast.error("Email and name are required");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (!selectedRole) {
      toast.error("Please select a role");
      return;
    }

    createUser.mutate(
      {
        email: email.trim(),
        name: name.trim(),
        position: position.trim() || undefined,
        role: selectedRole as AppRole,
      },
      {
        onSuccess: () => {
          resetForm();
          setIsCreateOpen(false);
        },
      }
    );
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!editingUser) return;

    if (!name) {
      toast.error("Name is required");
      return;
    }

    if (!selectedRole) {
      toast.error("Please select a role");
      return;
    }

    updateUser.mutate(
      {
        id: editingUser.user_id || editingUser.id,
        updates: {
          name: name.trim(),
          position: position.trim() || undefined,
          role: selectedRole as AppRole,
        },
      },
      {
        onSuccess: () => {
          resetForm();
          setEditingUser(null);
          setIsEditOpen(false);
        },
      }
    );
  };

  const openEditDialog = (user: UserWithRoles) => {
    setEditingUser(user);
    setEmail(user.email);
    setName(user.name);
    setPosition(user.position || "");
    const role = user.roles?.[0] ?? (user as { role?: AppRole }).role;
    setSelectedRole((role as AppRole) ?? "");
    setIsEditOpen(true);
  };

  const handleStatusChangeClick = (user: UserWithRoles, newActive: boolean) => {
    const userId = user.user_id || user.id;
    const isSelf = currentUser && (userId === currentUser.id);
    if (isSelf && !newActive) {
      toast.error("You cannot deactivate your own account");
      return;
    }
    setPendingStatusChange({ user, newActive });
  };

  const handleConfirmStatusChange = async () => {
    if (!pendingStatusChange) return;
    await updateUserStatus(pendingStatusChange.user.user_id || pendingStatusChange.user.id, pendingStatusChange.newActive);
    setPendingStatusChange(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogTrigger asChild>
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Add User
          </Button>
        </DialogTrigger>
        <DialogContent>
          <form onSubmit={handleCreate}>
            <DialogHeader>
              <DialogTitle>Add User</DialogTitle>
              <DialogDescription>
                Create a new user account. A temporary password will be sent to their
                email.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="user@example.com"
                  className="mt-1"
                  autoFocus
                />
              </div>
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="position">Position</Label>
                <Input
                  id="position"
                  value={position}
                  onChange={(e) => setPosition(e.target.value)}
                  placeholder="Software Engineer"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="mb-3 block">Roles *</Label>
                <RadioGroup
                  value={selectedRole}
                  onValueChange={(val) => setSelectedRole(val as AppRole)}
                  className="flex flex-col gap-2"
                >
                  {availableRoles.map((role) => (
                    <div key={role} className="flex items-center space-x-2">
                      <RadioGroupItem value={role} id={`role-${role}`} />
                      <Label
                        htmlFor={`role-${role}`}
                        className="text-sm font-medium leading-none cursor-pointer capitalize"
                      >
                        {role.replace("_", " ")}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  resetForm();
                  setIsCreateOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createUser.isPending}>
                {createUser.isPending && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                Create User
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent>
          <form onSubmit={handleEdit}>
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
              <DialogDescription>
                Update user information and roles
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={email}
                  disabled
                  className="mt-1 bg-muted"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Email cannot be changed
                </p>
              </div>
              <div>
                <Label htmlFor="edit-name">Name *</Label>
                <Input
                  id="edit-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1"
                  autoFocus
                />
              </div>
              <div>
                <Label htmlFor="edit-position">Position</Label>
                <Input
                  id="edit-position"
                  value={position}
                  onChange={(e) => setPosition(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="mb-3 block">Roles *</Label>
                <RadioGroup
                  value={selectedRole}
                  onValueChange={(val) => setSelectedRole(val as AppRole)}
                  className="flex flex-col gap-2"
                >
                  {availableRoles.map((role) => (
                    <div key={role} className="flex items-center space-x-2">
                      <RadioGroupItem value={role} id={`edit-role-${role}`} />
                      <Label
                        htmlFor={`edit-role-${role}`}
                        className="text-sm font-medium leading-none cursor-pointer capitalize"
                      >
                        {role.replace("_", " ")}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  resetForm();
                  setEditingUser(null);
                  setIsEditOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={updateUser.isPending}>
                {updateUser.isPending && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                Update User
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Position</TableHead>
              <TableHead>Roles</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  No users yet. Create one to get started.
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.position || "-"}</TableCell>
                  <TableCell>
                    <div className="flex gap-1 flex-wrap">
                      {user.roles?.map((role) => (
                        <Badge key={role} variant="secondary" className="text-xs">
                          {role.replace("_", " ")}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={user.is_active !== false}
                        onCheckedChange={(checked) => handleStatusChangeClick(user, checked)}
                        disabled={currentUser != null && (user.user_id || user.id) === currentUser.id}
                      />
                      <span className="text-sm">
                        {user.is_active !== false ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(user)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={pendingStatusChange != null} onOpenChange={(open) => !open && setPendingStatusChange(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingStatusChange?.newActive ? "Activate user" : "Deactivate user"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingStatusChange?.newActive
                ? `Are you sure you want to activate ${pendingStatusChange.user.name}? They will be able to sign in again.`
                : `Are you sure you want to deactivate ${pendingStatusChange?.user.name}? They will no longer be able to sign in.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingStatusChange(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmStatusChange}>
              {pendingStatusChange?.newActive ? "Activate" : "Deactivate"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
